import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Building2, FileText, Users, DollarSign, Clock, X, Check, Ban, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem("academiaos_token") || localStorage.getItem("token");
}
function authHeaders() {
  return { Authorization: `Bearer ${getToken()}` };
}

// ── DB-constrained enums ───────────────────────────────────────────────────────
const SPONSOR_TYPES    = ["GOVERNMENT","EMPLOYER","CORPORATE","FOUNDATION","EMBASSY","CHARITY","OTHER"];
const COVERAGE_TYPES   = ["FULL_FEE","TUITION_ONLY","PARTIAL_AMOUNT","PARTIAL_PERCENTAGE"];
const COVERAGE_BASIS   = ["FIXED_AMOUNT","FULL_FEE","PARTIAL_PERCENTAGE"];
const PAYMENT_TERMS    = ["NET_30","NET_60","NET_90","OTHER"];
const INV_FREQUENCY    = ["PER_STUDENT","PER_TERM","ANNUAL_BATCH"];
const TERMINATION_CODES = ["STUDENT_REINSTATED","INSTITUTION_ABSORBED","AGREEMENT_EXPIRED","SPONSOR_WITHDRAWAL","OTHER"];

// ── Role helpers ───────────────────────────────────────────────────────────────
// user.role is a single string (e.g. "institution_admin"), NOT an array.
function hasRole(role, ...allowed) {
  return allowed.includes(role);
}

// ── Status colours ─────────────────────────────────────────────────────────────
const STATUS_COLOR = {
  ACTIVE:             "text-emerald-400",
  DRAFT:              "text-blue-400",
  SUBMITTED:          "text-amber-400",
  SUSPENDED:          "text-orange-400",
  TERMINATED:         "text-red-400",
  CLOSED:             "text-slate-400",
  INACTIVE:           "text-slate-500",
  OPEN:               "text-amber-400",
  PARTIALLY_RECEIVED: "text-blue-400",
  FULLY_RECEIVED:     "text-emerald-400",
  WRITTEN_OFF:        "text-red-400",
};
function StatusBadge({ status }) {
  return (
    <span className={`text-xs font-mono ${STATUS_COLOR[status] || "text-slate-400"}`}>
      {status}
    </span>
  );
}

// ── Tabs ───────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "sponsors",    label: "Sponsors",       icon: Building2  },
  { id: "agreements",  label: "Agreements",     icon: FileText   },
  { id: "assignments", label: "Assignments",    icon: Users      },
  { id: "receivables", label: "Receivables",    icon: DollarSign },
  { id: "queue",       label: "Approval Queue", icon: Clock      },
];

// ── Sponsor Master Panel ───────────────────────────────────────────────────────
function SponsorsPanel({ canConfigure }) {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    sponsor_code: "", display_name: "", legal_name: "",
    sponsor_type: "EMPLOYER", country_code: "",
    effective_from: "", effective_to: "",
  });

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/finance/fm4b/sponsors`, { headers: authHeaders() });
      setSponsors(data);
    } catch { toast.error("Failed to load sponsors"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  async function create() {
    if (!form.sponsor_code || !form.display_name) {
      toast.error("Code and Display Name are required"); return;
    }
    try {
      await axios.post(`${API}/api/finance/fm4b/sponsors`, form, { headers: authHeaders() });
      toast.success("Sponsor created");
      setShowForm(false);
      setForm({ sponsor_code:"", display_name:"", legal_name:"", sponsor_type:"EMPLOYER", country_code:"", effective_from:"", effective_to:"" });
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create sponsor"); }
  }

  async function deactivate(id) {
    try {
      await axios.patch(`${API}/api/finance/fm4b/sponsors/${id}/deactivate`, {}, { headers: authHeaders() });
      toast.success("Sponsor deactivated");
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot deactivate — active assignments exist"); }
  }

  return (
    <div>
      {canConfigure && (
        <div className="flex justify-end mb-4">
          <button onClick={() => setShowForm(!showForm)} data-testid="add-sponsor-btn"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
            <Plus size={15} /> Add Sponsor
          </button>
        </div>
      )}

      {showForm && (
        <div className="mb-6 p-5 bg-slate-800/80 border border-slate-700 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white">New Sponsor</h3>
            <button onClick={() => setShowForm(false)}><X size={16} className="text-slate-400 hover:text-white" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[["sponsor_code","Code *"],["display_name","Display Name *"],["legal_name","Legal Name"],["country_code","Country Code"]].map(([k,lbl]) => (
              <div key={k}>
                <label className="block text-xs text-slate-400 mb-1">{lbl}</label>
                <input value={form[k]} onChange={e => setForm(f => ({...f,[k]:e.target.value}))}
                  className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500" />
              </div>
            ))}
            <div>
              <label className="block text-xs text-slate-400 mb-1">Type</label>
              <select value={form.sponsor_type} onChange={e => setForm(f => ({...f,sponsor_type:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white">
                {SPONSOR_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Effective From</label>
              <input type="date" value={form.effective_from} onChange={e => setForm(f => ({...f,effective_from:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Effective To</label>
              <input type="date" value={form.effective_to} onChange={e => setForm(f => ({...f,effective_to:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white" />
            </div>
          </div>
          <button onClick={create} data-testid="sponsor-save-btn"
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
            Save Sponsor
          </button>
        </div>
      )}

      {loading ? <p className="text-slate-400 text-sm">Loading...</p> : (
        <div className="space-y-2">
          {sponsors.map(s => (
            <div key={s.id} data-testid={`sponsor-row-${s.id}`}
              className="flex items-center justify-between p-4 bg-slate-800/60 border border-slate-700/80 rounded-xl hover:border-slate-600 transition-colors">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium text-sm">{s.display_name}</span>
                  <span className="text-xs text-slate-500 font-mono">{s.sponsor_code}</span>
                  <StatusBadge status={s.status} />
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {s.sponsor_type}{s.country_code ? ` · ${s.country_code}` : ""}
                  {s.effective_from ? ` · ${s.effective_from}` : ""}
                  {s.effective_to ? ` → ${s.effective_to}` : ""}
                </div>
              </div>
              {canConfigure && s.status === "ACTIVE" && (
                <button onClick={() => deactivate(s.id)}
                  className="ml-4 shrink-0 text-xs text-slate-400 hover:text-red-400 transition-colors">
                  Deactivate
                </button>
              )}
            </div>
          ))}
          {sponsors.length === 0 && <p className="text-slate-500 text-sm py-8 text-center">No sponsors registered yet.</p>}
        </div>
      )}
    </div>
  );
}

// ── Agreements Panel ───────────────────────────────────────────────────────────
function AgreementsPanel({ canConfigure }) {
  const [agreements, setAgreements] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    sponsor_id: "", effective_from: "", effective_to: "",
    coverage_type: "FULL_FEE", max_coverage_amount: "",
    invoice_frequency: "PER_STUDENT", payment_due_terms: "NET_30",
  });

  const fetchAll = useCallback(async () => {
    try {
      const [a, s] = await Promise.all([
        axios.get(`${API}/api/finance/fm4b/sponsor-agreements`, { headers: authHeaders() }),
        axios.get(`${API}/api/finance/fm4b/sponsors`, { headers: authHeaders() }),
      ]);
      setAgreements(a.data); setSponsors(s.data.filter(x => x.status === "ACTIVE"));
    } catch { toast.error("Failed to load agreements"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function create() {
    if (!form.sponsor_id || !form.effective_from || !form.coverage_type) {
      toast.error("Sponsor, effective date, and coverage type are required"); return;
    }
    try {
      await axios.post(`${API}/api/finance/fm4b/sponsor-agreements`, form, { headers: authHeaders() });
      toast.success("Agreement created (DRAFT)");
      setShowForm(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create agreement"); }
  }

  async function activate(id) {
    try {
      await axios.patch(`${API}/api/finance/fm4b/sponsor-agreements/${id}/activate`, {}, { headers: authHeaders() });
      toast.success("Agreement activated — now ACTIVE");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to activate"); }
  }

  return (
    <div>
      {canConfigure && (
        <div className="flex justify-end mb-4">
          <button onClick={() => setShowForm(!showForm)} data-testid="add-agreement-btn"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
            <Plus size={15} /> New Agreement
          </button>
        </div>
      )}

      {showForm && (
        <div className="mb-6 p-5 bg-slate-800/80 border border-slate-700 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white">New Sponsor Agreement</h3>
            <button onClick={() => setShowForm(false)}><X size={16} className="text-slate-400 hover:text-white" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Sponsor *</label>
              <select value={form.sponsor_id} onChange={e => setForm(f => ({...f,sponsor_id:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white">
                <option value="">Select active sponsor</option>
                {sponsors.map(s => <option key={s.id} value={s.id}>{s.display_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Coverage Type *</label>
              <select value={form.coverage_type} onChange={e => setForm(f => ({...f,coverage_type:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white">
                {COVERAGE_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Effective From *</label>
              <input type="date" value={form.effective_from} onChange={e => setForm(f => ({...f,effective_from:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Effective To</label>
              <input type="date" value={form.effective_to} onChange={e => setForm(f => ({...f,effective_to:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Max Coverage Amount</label>
              <input type="number" value={form.max_coverage_amount} onChange={e => setForm(f => ({...f,max_coverage_amount:e.target.value}))}
                placeholder="e.g. 500000" className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Invoice Frequency</label>
              <select value={form.invoice_frequency} onChange={e => setForm(f => ({...f,invoice_frequency:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white">
                {INV_FREQUENCY.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Payment Terms</label>
              <select value={form.payment_due_terms} onChange={e => setForm(f => ({...f,payment_due_terms:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white">
                {PAYMENT_TERMS.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <button onClick={create} data-testid="agreement-save-btn"
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
            Create Agreement
          </button>
        </div>
      )}

      {loading ? <p className="text-slate-400 text-sm">Loading...</p> : (
        <div className="space-y-2">
          {agreements.map(a => (
            <div key={a.id} data-testid={`agreement-row-${a.id}`}
              className="flex items-center justify-between p-4 bg-slate-800/60 border border-slate-700/80 rounded-xl hover:border-slate-600 transition-colors">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium text-sm">{a.agreement_ref}</span>
                  <StatusBadge status={a.status} />
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {a.sponsor_name} · {a.coverage_type} · {a.payment_due_terms}
                  {a.max_coverage_amount ? ` · Max ₹${Number(a.max_coverage_amount).toLocaleString()}` : ""}
                  {a.effective_from ? ` · ${a.effective_from}` : ""}
                </div>
              </div>
              {canConfigure && a.status === "DRAFT" && (
                <button onClick={() => activate(a.id)} data-testid={`activate-agreement-${a.id}`}
                  className="ml-4 shrink-0 flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                  <Check size={13} /> Activate
                </button>
              )}
            </div>
          ))}
          {agreements.length === 0 && <p className="text-slate-500 text-sm py-8 text-center">No agreements yet.</p>}
        </div>
      )}
    </div>
  );
}

// ── Assignments Panel ──────────────────────────────────────────────────────────
function AssignmentsPanel({ canAssign, canApprove, canTerminate }) {
  const [assignments, setAssignments] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ agreement_id:"", student_account_id:"", coverage_amount:"", coverage_basis:"FIXED_AMOUNT" });
  const [terminateTarget, setTerminateTarget] = useState(null);
  const [terminateForm, setTerminateForm] = useState({ termination_reason_code:"STUDENT_REINSTATED", termination_reason_text:"" });

  const fetchAll = useCallback(async () => {
    try {
      const [a, ag] = await Promise.all([
        axios.get(`${API}/api/finance/fm4b/sponsorship-assignments`, { headers: authHeaders() }),
        axios.get(`${API}/api/finance/fm4b/sponsor-agreements`, { headers: authHeaders() }),
      ]);
      setAssignments(a.data); setAgreements(ag.data.filter(x => x.status === "ACTIVE"));
    } catch { toast.error("Failed to load assignments"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function create() {
    if (!form.agreement_id || !form.student_account_id || !form.coverage_amount) {
      toast.error("Agreement, student account ID, and coverage amount are required"); return;
    }
    try {
      await axios.post(`${API}/api/finance/fm4b/sponsorship-assignments`, form, { headers: authHeaders() });
      toast.success("Assignment created (DRAFT)");
      setShowForm(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create assignment"); }
  }

  async function submit(id) {
    try {
      await axios.patch(`${API}/api/finance/fm4b/sponsorship-assignments/${id}/submit`, {}, { headers: authHeaders() });
      toast.success("Submitted for approval");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  async function approve(id) {
    try {
      await axios.patch(`${API}/api/finance/fm4b/sponsorship-assignments/${id}/approve`,
        { reason: "Approved via staff interface" }, { headers: authHeaders() });
      toast.success("Assignment approved and ACTIVE — sponsor receivable created");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to approve"); }
  }

  async function terminate() {
    if (!terminateForm.termination_reason_text) {
      toast.error("Reason text is required for termination"); return;
    }
    try {
      await axios.patch(`${API}/api/finance/fm4b/sponsorship-assignments/${terminateTarget}/terminate`,
        terminateForm, { headers: authHeaders() });
      toast.success("Assignment terminated — student liability restored");
      setTerminateTarget(null);
      setTerminateForm({ termination_reason_code:"STUDENT_REINSTATED", termination_reason_text:"" });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to terminate"); }
  }

  return (
    <div>
      {canAssign && (
        <div className="flex justify-end mb-4">
          <button onClick={() => setShowForm(!showForm)} data-testid="add-assignment-btn"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
            <Plus size={15} /> New Assignment
          </button>
        </div>
      )}

      {showForm && (
        <div className="mb-6 p-5 bg-slate-800/80 border border-slate-700 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white">New Sponsorship Assignment</h3>
            <button onClick={() => setShowForm(false)}><X size={16} className="text-slate-400" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Agreement *</label>
              <select value={form.agreement_id} onChange={e => setForm(f => ({...f,agreement_id:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white">
                <option value="">Select active agreement</option>
                {agreements.map(a => <option key={a.id} value={a.id}>{a.agreement_ref} ({a.sponsor_name})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Student Account ID *</label>
              <input value={form.student_account_id} onChange={e => setForm(f => ({...f,student_account_id:e.target.value}))}
                placeholder="UUID from student accounts" className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Coverage Amount *</label>
              <input type="number" value={form.coverage_amount} onChange={e => setForm(f => ({...f,coverage_amount:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Coverage Basis</label>
              <select value={form.coverage_basis} onChange={e => setForm(f => ({...f,coverage_basis:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white">
                {COVERAGE_BASIS.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <button onClick={create} data-testid="assignment-save-btn"
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
            Create Assignment
          </button>
        </div>
      )}

      {terminateTarget && (
        <div className="mb-4 p-4 bg-red-950/60 border border-red-800/60 rounded-xl">
          <h3 className="text-sm font-semibold text-red-300 mb-3">Terminate Assignment</h3>
          <p className="text-xs text-red-400/80 mb-3">
            Termination restores the student's full liability. This action requires a documented reason.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Reason Code</label>
              <select value={terminateForm.termination_reason_code}
                onChange={e => setTerminateForm(f => ({...f,termination_reason_code:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white">
                {TERMINATION_CODES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Reason Text *</label>
              <input value={terminateForm.termination_reason_text}
                onChange={e => setTerminateForm(f => ({...f,termination_reason_text:e.target.value}))}
                placeholder="Required — documented reason for termination"
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white" />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={terminate} data-testid="terminate-confirm-btn"
              className="px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors">
              Confirm Termination
            </button>
            <button onClick={() => setTerminateTarget(null)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? <p className="text-slate-400 text-sm">Loading...</p> : (
        <div className="space-y-2">
          {assignments.map(a => (
            <div key={a.id} data-testid={`assignment-row-${a.id}`}
              className="flex items-center justify-between p-4 bg-slate-800/60 border border-slate-700/80 rounded-xl hover:border-slate-600 transition-colors">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium text-sm">{a.assignment_ref}</span>
                  <StatusBadge status={a.status} />
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {a.sponsor_name} · {a.coverage_basis} · ₹{Number(a.coverage_amount).toLocaleString()}
                </div>
              </div>
              <div className="flex items-center gap-3 ml-4 shrink-0">
                {/* Submit: DRAFT only, canAssign */}
                {canAssign && a.status === "DRAFT" && (
                  <button onClick={() => submit(a.id)} data-testid={`submit-assignment-${a.id}`}
                    className="text-xs text-amber-400 hover:text-amber-300 transition-colors">
                    Submit
                  </button>
                )}
                {/* Approve: DRAFT or SUBMITTED, canApprove */}
                {canApprove && (a.status === "DRAFT" || a.status === "SUBMITTED") && (
                  <button onClick={() => approve(a.id)} data-testid={`approve-assignment-${a.id}`}
                    className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                    <Check size={13} /> Approve
                  </button>
                )}
                {/* Terminate: ACTIVE only, canTerminate */}
                {canTerminate && a.status === "ACTIVE" && (
                  <button onClick={() => setTerminateTarget(a.id)} data-testid={`terminate-assignment-${a.id}`}
                    className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors">
                    <Ban size={13} /> Terminate
                  </button>
                )}
              </div>
            </div>
          ))}
          {assignments.length === 0 && <p className="text-slate-500 text-sm py-8 text-center">No assignments yet.</p>}
        </div>
      )}
    </div>
  );
}

// ── Receivables Panel ──────────────────────────────────────────────────────────
function ReceivablesPanel({ canRecordPayment }) {
  const [receivables, setReceivables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ amount:"", payment_date:"", fm3_payment_id:"", payment_reference:"", notes:"" });

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/finance/fm4b/sponsor-receivables`, { headers: authHeaders() });
      setReceivables(data);
    } catch { toast.error("Failed to load receivables"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  async function recordPayment() {
    if (!payForm.amount) { toast.error("Amount is required"); return; }
    try {
      const payload = {
        amount: payForm.amount,
        notes: payForm.notes || undefined,
        payment_reference: payForm.payment_reference || undefined,
        payment_date: payForm.payment_date || undefined,
        fm3_payment_id: payForm.fm3_payment_id || undefined,
      };
      const { data } = await axios.post(
        `${API}/api/finance/fm4b/sponsor-receivables/${payTarget}/record-payment`,
        payload, { headers: authHeaders() }
      );
      if (data.is_cash) {
        toast.success("Cash receipt recorded — received amount updated");
      } else {
        toast.success("Admin reference noted — no change to received amount (no FM3 payment ID)");
      }
      setPayTarget(null);
      setPayForm({ amount:"", payment_date:"", fm3_payment_id:"", payment_reference:"", notes:"" });
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to record payment"); }
  }

  return (
    <div>
      {/* Payment entry form */}
      {payTarget && (
        <div className="mb-4 p-5 bg-slate-800/80 border border-slate-700 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">Record Sponsor Payment</h3>
            <button onClick={() => setPayTarget(null)}><X size={15} className="text-slate-400" /></button>
          </div>
          <div className="mb-3 p-3 bg-amber-950/30 border border-amber-800/30 rounded-lg">
            <p className="text-xs text-amber-300">
              <strong>FM3 Payment ID is required for cash receipts.</strong> Without it, this is an
              admin reference only — the received amount will NOT be updated.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Amount *</label>
              <input type="number" value={payForm.amount} onChange={e => setPayForm(f => ({...f,amount:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Payment Date</label>
              <input type="date" value={payForm.payment_date} onChange={e => setPayForm(f => ({...f,payment_date:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">FM3 Payment ID <span className="text-slate-600">(for cash receipts)</span></label>
              <input value={payForm.fm3_payment_id} onChange={e => setPayForm(f => ({...f,fm3_payment_id:e.target.value}))}
                placeholder="UUID — leave blank for admin reference"
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white font-mono text-xs" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Payment Reference</label>
              <input value={payForm.payment_reference} onChange={e => setPayForm(f => ({...f,payment_reference:e.target.value}))}
                placeholder="Wire ref, cheque no., etc."
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-slate-400 mb-1">Notes</label>
              <input value={payForm.notes} onChange={e => setPayForm(f => ({...f,notes:e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white" />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={recordPayment} data-testid="record-payment-btn"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
              {payForm.fm3_payment_id ? "Record Cash Receipt" : "Save Admin Reference"}
            </button>
            <button onClick={() => setPayTarget(null)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? <p className="text-slate-400 text-sm">Loading...</p> : (
        <div className="space-y-2">
          {receivables.map(r => (
            <div key={r.id} data-testid={`receivable-row-${r.id}`}
              className="p-4 bg-slate-800/60 border border-slate-700/80 rounded-xl hover:border-slate-600 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-medium text-sm">{r.receivable_ref}</span>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">{r.sponsor_name} · {r.agreement_ref}</div>
                </div>
                {canRecordPayment && !["FULLY_RECEIVED","CLOSED","WRITTEN_OFF"].includes(r.status) && (
                  <button onClick={() => setPayTarget(r.id)} data-testid={`record-payment-open-${r.id}`}
                    className="shrink-0 text-xs text-blue-400 hover:text-blue-300 transition-colors">
                    Record Payment
                  </button>
                )}
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                <div className="bg-slate-900/60 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-slate-500 mb-0.5">Expected</p>
                  <p className="text-sm text-white font-medium">₹{Number(r.expected_amount).toLocaleString()}</p>
                </div>
                <div className="bg-slate-900/60 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-slate-500 mb-0.5">Received</p>
                  <p className="text-sm text-emerald-400 font-medium">₹{Number(r.received_amount).toLocaleString()}</p>
                </div>
                <div className="bg-slate-900/60 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-slate-500 mb-0.5">Outstanding</p>
                  <p className={`text-sm font-medium ${Number(r.outstanding) > 0 ? "text-amber-400" : "text-slate-400"}`}>
                    ₹{Number(r.outstanding).toLocaleString()}
                  </p>
                </div>
              </div>
              {r.due_date && (
                <p className="mt-2 text-xs text-slate-500">Due: {r.due_date}</p>
              )}
            </div>
          ))}
          {receivables.length === 0 && <p className="text-slate-500 text-sm py-8 text-center">No receivables yet.</p>}
        </div>
      )}
    </div>
  );
}

// ── Approval Queue Panel ───────────────────────────────────────────────────────
function ApprovalQueuePanel({ canApprove }) {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/finance/fm4b/approval-queue`, { headers: authHeaders() });
      setQueue(data);
    } catch { toast.error("Failed to load approval queue"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  async function approve(id) {
    try {
      await axios.patch(`${API}/api/finance/fm4b/sponsorship-assignments/${id}/approve`,
        { reason: "Approved via queue" }, { headers: authHeaders() });
      toast.success("Assignment approved — ACTIVE, sponsor receivable created");
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Approval failed"); }
  }

  return (
    <div>
      {loading ? <p className="text-slate-400 text-sm">Loading...</p> : (
        <>
          {queue.length > 0 && (
            <div className="mb-4 p-3 bg-amber-950/30 border border-amber-800/30 rounded-lg">
              <p className="text-xs text-amber-300">
                {queue.length} assignment{queue.length > 1 ? "s" : ""} pending approval.
                Approving activates sponsorship and creates a canonical sponsor receivable.
              </p>
            </div>
          )}
          <div className="space-y-2">
            {queue.map(item => (
              <div key={item.id} data-testid={`queue-item-${item.id}`}
                className="flex items-center justify-between p-4 bg-slate-800/60 border border-slate-700/80 rounded-xl hover:border-slate-600 transition-colors">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-medium text-sm">{item.ref}</span>
                    <StatusBadge status={item.status} />
                    <span className="text-xs text-slate-500">{item.type}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {item.sponsor_name} · {item.coverage_basis} · ₹{Number(item.coverage_amount).toLocaleString()}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">Submitted: {new Date(item.created_at).toLocaleDateString()}</div>
                </div>
                {canApprove && (
                  <button onClick={() => approve(item.id)} data-testid={`queue-approve-${item.id}`}
                    className="ml-4 shrink-0 flex items-center gap-1 px-3 py-1.5 bg-emerald-700/40 hover:bg-emerald-700/60 border border-emerald-700/40 text-emerald-300 rounded-lg text-xs font-medium transition-colors">
                    <Check size={12} /> Approve
                  </button>
                )}
              </div>
            ))}
            {queue.length === 0 && (
              <div className="text-center py-12">
                <Check size={32} className="text-emerald-700 mx-auto mb-3 opacity-50" />
                <p className="text-slate-500 text-sm">No sponsorships pending approval.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function SponsorshipsAdmin() {
  const [activeTab, setActiveTab] = useState("sponsors");

  // user.role is a single string — use direct inclusion check
  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : {};
  const role = user.role || "";

  const canConfigure    = hasRole(role, "institution_admin", "super_admin");
  const canAssign       = hasRole(role, "finance_manager", "institution_admin", "super_admin");
  const canApprove      = hasRole(role, "cfo", "institution_admin", "super_admin");
  const canTerminate    = hasRole(role, "cfo", "institution_admin", "super_admin");
  const canRecordPayment= hasRole(role, "finance_manager", "cfo", "institution_admin", "super_admin");

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Sponsorships</h1>
          <p className="text-slate-400 mt-1 text-sm">
            Manage sponsor entities, agreements, student assignments, and receivables.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-slate-900 rounded-xl p-1 w-fit overflow-x-auto">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id ? "bg-slate-700 text-white" : "text-slate-400 hover:text-white"
              }`}>
              <tab.icon size={15} />
              {tab.label}
              {tab.id === "queue" && <QueueBadge />}
            </button>
          ))}
        </div>

        <div data-testid="sponsorships-admin-content">
          {activeTab === "sponsors"    && <SponsorsPanel canConfigure={canConfigure} />}
          {activeTab === "agreements"  && <AgreementsPanel canConfigure={canConfigure} />}
          {activeTab === "assignments" && <AssignmentsPanel canAssign={canAssign} canApprove={canApprove} canTerminate={canTerminate} />}
          {activeTab === "receivables" && <ReceivablesPanel canRecordPayment={canRecordPayment} />}
          {activeTab === "queue"       && <ApprovalQueuePanel canApprove={canApprove} />}
        </div>
      </div>
    </div>
  );
}

// Small badge showing pending count on queue tab
function QueueBadge() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    axios.get(`${API}/api/finance/fm4b/approval-queue`, { headers: authHeaders() })
      .then(r => setCount(r.data.length))
      .catch(() => {});
  }, []);
  if (!count) return null;
  return (
    <span className="ml-1 px-1.5 py-0.5 bg-amber-500 text-slate-900 rounded-full text-[9px] font-bold leading-none">
      {count}
    </span>
  );
}
